'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive FieldSet
 * ROLE: group (implicit via <fieldset>)
 * ARIA: <legend> labels the group; aria-describedby threads description + error;
 *       aria-invalid="true" when error.
 * LIVE-REGION: FieldSet.Error has aria-live="polite".
 * FOCUS: when error appears, focus moves to the first interactive descendant
 *        (handled by parent <Form> wrapper).
 * RSC: CLIENT.
 * SUBSTRATE: native HTML5 fieldset/legend.
 */
import { useId } from 'react';
import { Caption } from '../../../server/typography/Caption/Caption';
import { FieldSetDescription } from './FieldSetDescription';
import { FieldSetError } from './FieldSetError';
import { FieldSetLegend } from './FieldSetLegend';
import styles from './FieldSet.module.css';
function FieldSetInner({ legend, legendTone = 'label', description, error, children, }) {
    const baseId = useId();
    const descriptionId = description != null ? `${baseId}-desc` : undefined;
    const hasError = error != null && error !== false && error !== '';
    const errorId = hasError ? `${baseId}-err` : undefined;
    const describedByParts = [descriptionId, errorId].filter(Boolean);
    const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
    return (_jsxs("fieldset", { className: styles.fieldset, "aria-describedby": describedBy, "aria-invalid": hasError ? true : undefined, children: [legend != null && (_jsx("legend", { className: styles.legend, "data-tone": legendTone, children: legend })), _jsx("div", { className: styles.controls, children: children }), description != null && (_jsx(Caption, { id: descriptionId, children: description })), hasError && (_jsx("span", { id: errorId, "aria-live": "polite", style: { font: 'var(--type-caption)', color: 'var(--destructive)' }, children: error }))] }));
}
/**
 * Grouped control field set. Use for radio groups and multi-checkbox groups.
 *
 * ```tsx
 * <FieldSet legend="Notification preference" error={errors.notif}>
 *   <RadioGroup value={pref} onValueChange={setPref}>
 *     <Radio value="all">All</Radio>
 *     <Radio value="mentions">Mentions only</Radio>
 *   </RadioGroup>
 * </FieldSet>
 * ```
 */
export const FieldSet = Object.assign(FieldSetInner, {
    Legend: Object.assign(FieldSetLegend, { displayName: 'FieldSet.Legend' }),
    Description: Object.assign(FieldSetDescription, { displayName: 'FieldSet.Description' }),
    Error: Object.assign(FieldSetError, { displayName: 'FieldSet.Error' }),
});
//# sourceMappingURL=FieldSet.js.map